// 表格单元格内的 FormModel 字段受控渲染组件（通用稳定组件，供各谈判表格复用）。
//
// ## 为什么需要这个组件（ProTablePlusForm custom-component 渲染机制）
//
// editRender({ name: 'custom-component', props: { customComp, ...rest } }) 经 TableExtend 的
// CellMPCRender = cellEditMemo(({ customComp: Comp, ...rest }) => <Comp {...rest} />) 渲染，
// cellEditMemo 对「渲染型函数 prop customComp」做引用比较：
//   - 内联箭头 customComp → 每次 editRender 都是新函数 → React 判定组件类型变化 →
//     单元格子树【卸载重挂载】(fiber 不复用) → 有状态控件丢状态 + 输入框抖动。
//   - 模块顶层稳定组件 → identity 恒定 → 类型不变 → fiber 复用，
//     仅当其余 props（引用比较的 row/modelConf/formItemKey 等）变化时轻量重渲染。
//
// 因此业务上"单元格 = 外层 form 字段"的编辑态应统一用本组件：
//   props: { customComp: FormModelCell, formItemKey: 'xxx', modelConf, ... }
//   （formItemKey 为 string 原始值，值比较恒等；modelConf 由调用方 useMemo 稳定更佳）
// 渲染机制沿用 BudgetOfferTable.renderFormCell 范式：IPCooperationDemandFormModel +
// renderType="row" 单字段 SFormItem → Form.Item(name=formItemKey) 注册 → 受控 value/onChange 注入，
// 编辑值自动写回外层 form 对应 key（getFieldsValue 可返回、提交可上送），无需手动 setFieldsValue。

import React from 'react';
import IPCooperationDemandFormModel from '../IPCooperationDemandFormModel';

/** FormModel 字段渲染配置（同 BudgetOfferTable/OfferModelConf：外层组装 itemCols/renderType 之外的配置项） */
export type FormModelCellConf = Omit<
  React.ComponentProps<typeof IPCooperationDemandFormModel>,
  'itemCols' | 'renderType'
>;

/** 出价列可编辑单元格字段渲染配置 */
export interface FormModelCellProps {
  /** 要渲染的 form model 字段 key（原始字符串，引用比较恒等不触发多余重渲染） */
  formItemKey?: string;
  /** FormModel 配置（含 renderData/globalItemProps.view；无则回退只读文本展示） */
  modelConf?: FormModelCellConf;
  /** SFormItem 栅格（缺省 24 占满整行，覆盖字段定义中的默认 col） */
  col?: number;
  /** 追加到单元格 FormItem 的 className（缺省隐藏 label 并撑满列宽） */
  className?: string;
}

const DEFAULT_CELL_CLASS = 'mb-0 [&_.rock-form-item-label]:hidden [&_.rock-col]:!w-full';

/** 单元格内 FormModel 字段稳定渲染组件（模块顶层，identity 恒定，规避 custom-component 内联闭包卸载重挂载）。 */
const FormModelCell: React.FC<FormModelCellProps> = ({
  formItemKey,
  modelConf,
  col = 24,
  className,
}) => {
  if (!modelConf || !formItemKey) return null;
  return (
    <IPCooperationDemandFormModel
      {...modelConf}
      renderType="row"
      // col 缺省 24：覆盖 FormModel 字段自带 col（如 license_regions/auth_duration 定义为 12）→ 单元格内部输入撑满
      itemCols={[{ formItemKey, col, className: className || DEFAULT_CELL_CLASS }]}
    />
  );
};

export default FormModelCell;
